import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import Login from "@/pages/Login";
import AppLayout from "@/components/AppLayout";
import Dashboard from "@/pages/Dashboard";
import Pipeline from "@/pages/Pipeline";
import Leads from "@/pages/Leads";
import Payments from "@/pages/Payments";
import Targets from "@/pages/Targets";
import Reports from "@/pages/Reports";
import Insights from "@/pages/Insights";
import Guide from "@/pages/Guide";
import Admin from "@/pages/Admin";
import Persetujuan from "@/pages/Persetujuan";
import Leaderboard from "@/pages/Leaderboard";
import TeamChat from "@/pages/TeamChat";
import KalkulatorStandalone from "@/pages/KalkulatorStandalone";
import DocumentPrint from "@/components/DocumentPrint";
import PublicDocument from "@/pages/PublicDocument";
import SplashScreen from "@/components/SplashScreen";

// silent=true skips the splash screen entirely — used for routes that open in their own
// fresh tab (like /cetak/:docId, opened via target="_blank") so opening a document doesn't
// show a full-screen loading moment. The splash is reserved for the one true entry point:
// signing in / refreshing the main app tab.
function Protected({ children, silent = false }) {
  const { user, loading } = useAuth();

  if (loading) return silent ? null : <SplashScreen />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function AdminOnly({ children }) {
  const { user } = useAuth();
  // Manager has nothing left in Pengaturan (Users/Cabang/Master/company data are all
  // admin/owner/chief-only) — their only reason to be here was Antrian Persetujuan,
  // which is now its own page, so they're excluded here but not from PersetujuanOnly.
  if (!["admin", "owner", "chief"].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function PersetujuanOnly({ children }) {
  const { user } = useAuth();
  if (!["admin", "owner", "chief", "manager"].includes(user?.role)) return <Navigate to="/dashboard" replace />;
  return children;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route element={<Protected><AppLayout /></Protected>}>
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/pipeline" element={<Pipeline />} />
        <Route path="/leads" element={<Leads />} />
        <Route path="/piutang" element={<Payments />} />
        <Route path="/target" element={<Targets />} />
        <Route path="/rekap" element={<Reports />} />
        <Route path="/insight" element={<Insights />} />
        <Route path="/leaderboard" element={<Leaderboard />} />
        <Route path="/chat" element={<TeamChat />} />
        <Route path="/kalkulator" element={<KalkulatorStandalone />} />
        <Route path="/panduan" element={<Guide />} />
        <Route path="/admin" element={<AdminOnly><Admin /></AdminOnly>} />
        <Route path="/persetujuan" element={<PersetujuanOnly><Persetujuan /></PersetujuanOnly>} />
      </Route>
      <Route path="/cetak/:docId" element={<Protected silent><DocumentPrint /></Protected>} />
      {/* Public, no login — the link shared with the client themselves via WhatsApp/Email. */}
      <Route path="/dok/:docId" element={<PublicDocument />} />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App dark">
      <BrowserRouter>
        <AuthProvider>
          <AppRoutes />
          {/* mobileOffset pushes toasts below the iOS/Android status bar + notch — without
              it sonner's default fixed pixel offset sat toasts right under the physical
              clock/battery icons on phones, half-hidden and looking broken. */}
          <Toaster position="top-center" theme="dark" richColors mobileOffset={{ top: "max(env(safe-area-inset-top), 16px)", left: "16px", right: "16px" }} />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
